import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

/* ═══════════════════════════════════════════
   TfBtn — Primary action button (renders as Link)
   ═══════════════════════════════════════════ */

type BtnVariant = "primary" | "secondary" | "outline" | "navy" | "free";
type BtnSize = "sm" | "md" | "lg";

interface TfBtnProps {
  variant?: BtnVariant;
  size?: BtnSize;
  disabled?: boolean;
  fullWidth?: boolean;
}

const sizeMap = {
  sm: css`padding: 7px 16px; font-size: 0.8rem;`,
  md: css`padding: 10px 20px; font-size: 0.875rem;`,
  lg: css`padding: 14px 28px; font-size: 1rem;`,
};

const variantMap = {
  primary: css`
    background: var(--red);
    color: #fff;
    border: none;
    &:hover { background: var(--red-dark); transform: translateY(-1px); box-shadow: var(--shadow-red); }
  `,
  navy: css`
    background: var(--navy);
    color: #fff;
    border: none;
    &:hover { background: var(--navy-light); transform: translateY(-1px); box-shadow: var(--shadow-navy); }
  `,
  secondary: css`
    background: var(--card-bg);
    color: var(--navy);
    border: 1.5px solid var(--gray-200);
    &:hover { border-color: var(--navy); background: var(--gray-50); }
  `,
  outline: css`
    background: transparent;
    color: var(--navy);
    border: 1.5px solid var(--gray-200);
    &:hover { border-color: var(--navy); color: var(--navy-light); }
  `,
  free: css`
    background: linear-gradient(135deg, #0ea572, #0bc98a);
    color: #fff;
    border: none;
    &:hover { box-shadow: 0 4px 16px rgba(14,165,114,0.35); transform: translateY(-1px); }
  `,
};

export const TfBtn = styled(Link)<TfBtnProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-decoration: none;
  font-family: inherit;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  ${({ size = "md" }) => sizeMap[size]}
  ${({ variant = "primary" }) => variantMap[variant]}
  ${({ fullWidth }) => fullWidth && css`width: 100%; text-align: center;`}
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      background: var(--gray-400) !important;
      border-color: var(--gray-400) !important;
      color: var(--gray-600) !important;
    `}
`;

/* ═══════════════════════════════════════════
   TfBadge — Status badge
   ═══════════════════════════════════════════ */

type BadgeStatus = "open" | "closed" | "free" | "filling" | "sport" | "casadas";

const badgeColorMap: Record<BadgeStatus, { bg: string; color: string }> = {
  open:    { bg: "#0ea572",           color: "#fff" },
  closed:  { bg: "var(--red)",        color: "#fff" },
  free:    { bg: "#0ea572",           color: "#fff" },
  filling: { bg: "#f59e0b",           color: "var(--navy)" },
  sport:   { bg: "var(--red)",        color: "#fff" },
  casadas: { bg: "#7c3aed",           color: "#fff" },
};

export const TfBadge = styled.span<{ status: BadgeStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  font-family: var(--font-display);
  font-size: 0.55rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-radius: 6px;
  background: ${({ status }) => badgeColorMap[status].bg};
  color: ${({ status }) => badgeColorMap[status].color};
`;

/* ═══════════════════════════════════════════
   TfChip — Filter toggle chip
   ═══════════════════════════════════════════ */

export const TfChip = styled.button<{ active?: boolean; variant?: "red" }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  font-family: var(--font-body);
  font-size: 0.82rem;
  font-weight: 600;
  border-radius: 100px;
  cursor: pointer;
  white-space: nowrap;
  transition: var(--transition);
  user-select: none;
  border: 1.5px solid ${({ active }) => (active ? "var(--navy)" : "var(--gray-200)")};
  background: ${({ active, variant }) =>
    active
      ? variant === "red" ? "var(--red)" : "var(--navy)"
      : "var(--card-bg)"};
  color: ${({ active }) => (active ? "#fff" : "var(--gray-600)")};

  &:hover {
    border-color: var(--navy);
    color: ${({ active }) => (active ? "#fff" : "var(--navy)")};
  }
`;
